import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import { responderTokenObter, responderTokenEnviar } from "../services/api";

// Link mágico da empresa: chega por /responder/{token} (enviado pela eaiRH ao contato corporativo). O MESMO link é
// contextual: 1º acesso = RESPOSTA; se o candidato replicar depois, o link vira TRÉPLICA (última palavra). Tudo SEM
// criar conta, sem senha; cai na moderação antes de publicar.
export default function ResponderPage() {
  const { token } = useParams();
  const [estado, setEstado] = useState("carregando"); // carregando | ok | invalido | aguardando | encerrado | enviado
  const [relato, setRelato] = useState(null);
  const [texto, setTexto] = useState("");
  const [autor, setAutor] = useState("");
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");

  const ehTreplica = relato?.modo === "treplica";

  useEffect(() => {
    let vivo = true;
    responderTokenObter(token)
      .then((r) => {
        if (!vivo) return;
        setRelato(r);
        if (r.modo === "resposta" || r.modo === "treplica") setEstado("ok");
        else if (r.modo === "aguardando") setEstado("aguardando");
        else setEstado("encerrado"); // tréplica já dada / contraditório fechado
      })
      .catch(() => { if (vivo) setEstado("invalido"); });
    return () => { vivo = false; };
  }, [token]);

  const enviar = async () => {
    if (busy) return;
    if (!texto.trim()) { setErro(ehTreplica ? "Escreva a tréplica." : "Escreva a resposta."); return; }
    setBusy(true); setErro("");
    try {
      await responderTokenEnviar(token, texto.trim(), autor.trim());
      setEstado("enviado");
    } catch (e) {
      setErro(e.message || "Não foi possível enviar. Tente de novo.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-10">
        {estado === "carregando" && <p className="text-gray-500">Carregando…</p>}

        {estado === "invalido" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">⚠️</div>
            <h1 className="mt-2 text-xl font-bold text-red-700">Link inválido ou expirado</h1>
            {/* Quem chega aqui é quase sempre uma EMPRESA tentando responder — o pior desfecho possível é ela
                bater numa parede logo depois de decidir se manifestar. Antes a página mandava "peça um novo à
                equipe" sem dizer como; agora o pedido sai em um clique, com o assunto já escrito. */}
            <p className="mt-1 text-sm text-gray-600">
              Este link não é mais válido — ele pode ter sido substituído por um mais recente. Se você é a empresa e
              quer se manifestar sobre um relato, peça um link novo: respondemos no mesmo dia.
            </p>
            <a href="mailto:contato@eairh.com.br?subject=Preciso%20de%20um%20novo%20link%20de%20resposta&body=Ol%C3%A1!%20O%20link%20de%20resposta%20que%20recebi%20expirou.%20Podem%20me%20enviar%20um%20novo%3F%0A%0AEmpresa%3A%20%0ARelato%20(se%20souber%20o%20n%C3%BAmero)%3A%20"
              className="inline-block mt-4 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5">
              Pedir um link novo
            </a>
            <p className="mt-3 text-xs text-gray-500">
              Também dá para achar o relato pela empresa em <Link to="/empresas" className="text-primary font-semibold hover:underline">eairh.com.br/empresas</Link> —
              o direito de resposta é gratuito, sempre.
            </p>
          </div>
        )}

        {estado === "aguardando" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">✓</div>
            <h1 className="mt-2 text-xl font-bold text-green-700">Sua resposta já está publicada</h1>
            <p className="mt-1 text-sm text-gray-600">Obrigado! A resposta da empresa está no relato, ao lado do texto do candidato. Se o candidato se manifestar de novo, você recebe um aviso para a última palavra.</p>
            <Link to={`/relato/${relato?.id}`} className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Ver o relato →</Link>
          </div>
        )}

        {estado === "encerrado" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">✓</div>
            <h1 className="mt-2 text-xl font-bold text-green-700">Contraditório concluído</h1>
            <p className="mt-1 text-sm text-gray-600">As manifestações das duas partes já estão registradas no relato. Obrigado!</p>
            <Link to={`/relato/${relato?.id}`} className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Ver o relato →</Link>
          </div>
        )}

        {estado === "enviado" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">🙌</div>
            <h1 className="mt-2 text-xl font-bold text-primary">{ehTreplica ? "Tréplica recebida!" : "Resposta recebida!"}</h1>
            <p className="mt-2 text-sm text-gray-600">Obrigado. Passa por uma conferência rápida da equipe eaiRH e, em seguida, aparece publicamente no relato — com o mesmo peso das outras manifestações, e sem custo nenhum.</p>
            <p className="mt-2 text-xs text-gray-400">Responder bem costuma virar vitrine de marca empregadora. 💙</p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Conhecer o eaiRH →</Link>
          </div>
        )}

        {estado === "ok" && relato && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold text-primary uppercase tracking-wide">{ehTreplica ? "Tréplica · última palavra · eaiRH" : "Direito de resposta · eaiRH"}</p>
            <h1 className="mt-1 text-xl font-bold text-gray-900">{ehTreplica ? "Última palavra" : "Responder"} como {relato.empresa}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {ehTreplica
                ? "O candidato respondeu à sua resposta. Pelo direito de resposta, a empresa tem a palavra final. É público, gratuito e a gente nunca cobra pra esconder nada."
                : "Um candidato relatou a experiência dele em um processo seletivo. Aqui você conta a versão da empresa — é público, gratuito e a gente nunca cobra pra esconder nada."}
            </p>

            {/* O relato original */}
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-[11px] font-bold text-gray-500 uppercase mb-1">Relato do candidato</p>
              <p className="text-sm font-bold text-gray-800">{relato.titulo}</p>
              <p className="text-[11px] text-gray-500 mb-2">{relato.vaga ? `${relato.vaga} · ` : ""}{relato.etapa}</p>
              {relato.candidatoNome && (
                <p className="text-[11px] mb-2 rounded-lg bg-blue-50 border border-blue-100 px-2 py-1.5 text-blue-900">
                  👤 Candidato: <b>{relato.candidatoNome}</b>
                  <span className="block text-[10px] text-blue-700/80">Visível só para a empresa, para localizar o processo internamente. Não aparece em público.</span>
                </p>
              )}
              <p className="text-sm text-gray-700 whitespace-pre-line">{relato.descricao}</p>
            </div>

            {/* Contexto extra na TRÉPLICA: a resposta que a empresa já deu + a réplica do candidato */}
            {ehTreplica && relato.respostaTexto && (
              <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="text-[11px] font-bold text-blue-700 uppercase mb-1">Sua resposta (já publicada)</p>
                <p className="text-sm text-gray-700 whitespace-pre-line">{relato.respostaTexto}</p>
              </div>
            )}
            {ehTreplica && relato.contestacaoTexto && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-[11px] font-bold text-amber-700 uppercase mb-1">Réplica do candidato (responda a isto)</p>
                <p className="text-sm text-gray-700 whitespace-pre-line">{relato.contestacaoTexto}</p>
              </div>
            )}

            {relato.pendenteEmAnalise && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Já recebemos um texto seu para este relato e ele está em conferência. Se enviar de novo, substituímos o anterior.
              </p>
            )}

            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">{ehTreplica ? "Sua tréplica (última palavra)" : "Sua resposta"}</label>
              <textarea
                value={texto} onChange={(e) => setTexto(e.target.value)} rows={6} maxLength={5000}
                placeholder={ehTreplica
                  ? "Descreva com suas palavras o que aconteceu no processo — as datas, os combinados, o retorno. (O registro é só texto; não publicamos imagens, para proteger os dados de todas as partes.)"
                  : "Ex.: Obrigado pelo retorno. Nosso filtro considerou a localização atual do cadastro; como você indicou disponibilidade para São Paulo, vamos rever o processo e retomar o contato…"}
                className="w-full rounded-lg border border-gray-300 p-3 text-sm" />
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Quem está respondendo <span className="font-normal text-gray-400">(opcional — só a equipe eaiRH vê)</span></label>
                <input value={autor} onChange={(e) => setAutor(e.target.value)} maxLength={200}
                  placeholder="Ex.: Ricardo, R&S"
                  className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
              </div>
              {erro && <p className="mt-2 text-xs text-red-600">{erro}</p>}
              <button onClick={enviar} disabled={busy}
                className="mt-3 w-full rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
                {busy ? "Enviando…" : (ehTreplica ? "Enviar tréplica" : "Enviar resposta")}
              </button>
              <p className="mt-2 text-[11px] text-gray-400 text-center">Passa por uma conferência rápida antes de aparecer no relato. Sem criar conta. Só texto.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
